import React from 'react';
import { Button, Form, Modal } from 'react-bootstrap';

// ConfirmModal — the single shared confirmation dialog for consequential actions (Close, Delete,
// etc.) across the app: size="lg", backdrop="static", footer No (light) / {confirmLabel} (success).
// Parameterised by title/body/confirmLabel so every call site reuses this one component instead of
// hand-rolling its own near-identical modal. `danger` is the *trigger* elsewhere (e.g. the Delete
// dropdown item), never the confirm button here.
//
// **Return answers it**, because that is what Return does to a question. With no field, by FOCUSING the
// confirm as the dialog opens rather than by handling the key -- for the three reasons below, the last
// decisive. With a `field`, focus goes to the field instead and its own onKeyDown answers: the reason
// the key cannot be handled at the modal level is that a keydown on the container never reaches a
// listener on its child, and the field IS that child, receiving its own events directly.
//
// By FOCUSING the confirm as the dialog opens, rather than by handling the key. Three reasons, the
// last of which is decisive:
//
//  - a focus ring shows which answer Return will give, where a hidden handler would not;
//  - Space, and every other way a button is pressed, comes along for free;
//  - the key cannot be handled here anyway. `onKeyDown` on a react-bootstrap `<Modal>` lands on
//    `.modal-dialog` (Modal.js spreads the rest of its props onto the inner Dialog), while the
//    thing that HOLDS focus is the `.modal` container wrapping it. Events travel up, so a keydown
//    on the container never reaches a listener on its own child — it silently does nothing.
//
// `onEntered`, not `autoFocus`: the modal takes focus for itself when it opens, after its children
// have mounted, so a child's own autoFocus is given and then taken away.
//
// What this cannot do is confirm behind somebody's back. Focus is moved once, onto a button; a
// person who tabs to No gets No, and Escape still declines. The same arrangement the cell-delete
// dialog uses (missionControl/surfaces/cells/deleteCellDialog.js), which mirrors this component
// deliberately — the two are one family and answer the keyboard the same way.
export default function ConfirmModal({ show, onHide, onConfirm, title, body, confirmLabel = "Yes", field }) {
  const confirmRef = React.useRef(null);
  const fieldRef = React.useRef(null);
  const [value, setValue] = React.useState("");

  const unanswerable = field && field.required && !value.trim();

  const handleConfirm = () => {
    if ( unanswerable ) {
      return;
    }
    onConfirm(...(field ? [value] : []));
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static"
      // Cleared on EXIT, not entry: onEntered fires after the open transition, so clearing there
      // would flash the stale value during the fade-in, where onExited fires once already hidden.
      onExited={() => setValue("")}
      onEntered={() => {
        const target = field ? fieldRef.current : confirmRef.current;
        if (target) target.focus();
      }}>
      <Modal.Header closeButton>
        <Modal.Title>
          {title}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {body}
        {field && (
          <Form.Group className="mt-3">
            <Form.Label htmlFor="confirm-modal-field">{field.label}</Form.Label>
            <Form.Control id="confirm-modal-field" ref={fieldRef} type="text" value={value}
              placeholder={field.placeholder}
              onChange={(event) => setValue(event.target.value)}
              onKeyDown={(event) => { if ("Enter" === event.key) handleConfirm(); }}/>
            {field.note && <Form.Text className="text-muted">{field.note}</Form.Text>}
          </Form.Group>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onHide}>No</Button>
        <Button ref={confirmRef} variant="success" onClick={handleConfirm}
          disabled={!!unanswerable}>{confirmLabel}</Button>
      </Modal.Footer>
    </Modal>
  );
}
